import { type ComputedRef, computed, inject, type Ref, ref } from "vue";
import { useRoute } from "vue-router";
import { DESTINATIONS, type Destination } from "@/shared/contracts/destinations";
import { type ScreenSuggestion, useScreenSuggestionCatalog } from "../api/use-suggestions";

/**
 * O que perguntar ao Ben NESTA tela.
 *
 * ── POR QUE O CASAMENTO ACONTECE AQUI E NÃO NO SERVIDOR ───────────────────
 *
 * Porque a rota muda mais do que o catálogo. O painel carrega a lista inteira uma
 * vez (dezenas de linhas curtas) e reencontra o grupo a cada navegação sem tocar
 * na rede — que é o que faz a sugestão trocar no MESMO quadro em que a tela
 * troca. Uma consulta por `?path=` desenharia botões da tela anterior por um
 * instante em toda navegação, e é o tipo de atraso que se lê como bug.
 *
 * É o desenho da V2 (`useBenSuggestions.forPath`), com a mesma regra de prefixo.
 */

/** O caminho que serve QUALQUER tela — o mesmo literal do servidor. */
export const ANY_SCREEN = "*";

/** Nada provido é resposta legítima: fora da moldura do painel não há menu. */
const NO_DESTINATIONS: Ref<readonly Destination[]> = ref([]);

export interface ScreenSuggestionsApi {
	/** As perguntas desta tela, já na ordem. Vazio é um estado normal. */
	readonly items: ComputedRef<readonly ScreenSuggestion[]>;
	/**
	 * O NOME da tela que casou, para o cabeçalho da lista — ou `null`.
	 *
	 * `null` não é falha: é "estas são as gerais", e o cabeçalho diz outra frase.
	 * Distinguir os dois é o que separa "Conversas" de "Para começar" na V2.
	 */
	readonly screenLabel: ComputedRef<string | null>;
	readonly loading: ComputedRef<boolean>;
}

/**
 * O caminho declarado que melhor serve `path`, ou `null`.
 *
 * Prefixo mais longo, e `'*'` fora da comparação — ele casaria com tudo e a tela
 * específica nunca ganharia. A barra é obrigatória na comparação de prefixo:
 * `/boardgame` começa com `/board` como TEXTO e é outra tela.
 */
export function matchScreen(path: string, declared: readonly string[]): string | null {
	let best: string | null = null;
	for (const candidate of declared) {
		if (candidate === ANY_SCREEN) continue;
		if (path !== candidate && !path.startsWith(`${candidate}/`)) continue;
		if (best === null || candidate.length > best.length) best = candidate;
	}
	return best;
}

/** As perguntas de um caminho, resolvidas contra o fallback global. */
export function suggestionsFor(
	path: string,
	all: readonly ScreenSuggestion[],
): { matched: string | null; items: readonly ScreenSuggestion[] } {
	const matched = matchScreen(
		path,
		all.map((s) => s.path),
	);
	const wanted = matched ?? ANY_SCREEN;
	const items = all.filter((s) => s.path === wanted).sort((a, b) => a.sortOrder - b.sortOrder);
	return { matched, items };
}

export function useScreenSuggestions(): ScreenSuggestionsApi {
	const route = useRoute();
	const destinations = inject(DESTINATIONS, NO_DESTINATIONS);
	const { data, isLoading } = useScreenSuggestionCatalog();

	const resolved = computed(() => suggestionsFor(route.path, data.value?.suggestions ?? []));

	return {
		items: computed(() => resolved.value.items),
		/**
		 * O rótulo vem do MENU, e não de uma coluna.
		 *
		 * Quem já nomeia toda tela do painel é `nav-items.ts`, e as entidades do
		 * tenant se nomeiam sozinhas — as duas chegam por `DESTINATIONS`. Guardar o
		 * nome da tela junto da pergunta seria a segunda lista de nomes, que
		 * envelhece na primeira vez que alguém renomeia um funil.
		 */
		screenLabel: computed(() => {
			const matched = resolved.value.matched;
			if (!matched) return null;
			return destinations.value.find((d) => d.to === matched)?.label ?? null;
		}),
		loading: computed(() => isLoading.value),
	};
}
